import { SUPPORT_EMAIL, VITE_FORGE_URL } from '@constants';
import forge from 'apis/forge';
import CreditTile from 'components/Account/CreditTile';
import Header from 'components/Account/Header';
import TierCard from 'components/Account/TierCard';
import Icon, { IconType } from 'components/Icon';
import ColorButton from 'components/Widget/Button/ColorButton';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import Chip from 'components/Widget/Chip';
import { Loader } from 'components/Widget/Loader';
import {
	OrganizationCreditWallet,
	OrganizationRole,
	OrganizationSubscription,
	Organization as OrganizationType,
	OrganizationUser,
	Tier,
} from 'dragonmouse-shared/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/db/reducers';
import { selectIsMobile } from 'redux/mode/reducers';
import { displayPromptModal } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import * as routes from 'routes';
import { useTheme } from 'styled-components';
import { getAvatarGradient, getInitials } from 'utils';
import * as Base from '../styles';
import * as S from './styles';

const formatDate = (date: Date | string): string => {
	try {
		const dateObj = typeof date === 'string' ? new Date(date) : date;
		if (isNaN(dateObj.getTime())) return '';
		return new Intl.DateTimeFormat('en-US', {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		}).format(dateObj);
	} catch {
		return '';
	}
};

const formatCredits = (credits: number): string => credits.toLocaleString();

const Organization = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { organizationId } = useParams<{ organizationId: string }>();

	const { isMobile, currentUser } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
			currentUser: selectUser(r.db),
		}),
		shallowEqual
	);

	const [loading, setLoading] = useState(true);
	const [organization, setOrganization] = useState<OrganizationType | null>(null);
	const [role, setRole] = useState<OrganizationRole | null>(null);
	const [users, setUsers] = useState<
		(OrganizationUser & { displayName?: string; email?: string })[]
	>([]);

	const [subscription, setSubscription] = useState<OrganizationSubscription | null>(null);
	const [creditWallet, setCreditWallet] = useState<OrganizationCreditWallet | null>(null);
	const [isYearly, setIsYearly] = useState(true);

	const [userEmail, setMemberEmail] = useState('');
	const [adding, setAdding] = useState(false);
	const [showInviteRow, setShowInviteRow] = useState(false);

	const isOwner = role === 'owner';

	useEffect(() => {
		if (!organizationId) return;

		const fetchData = async () => {
			try {
				const [orgResponse, usersResponse] = await Promise.all([
					forge.getOrganization(organizationId),
					forge.getOrganizationUsers(organizationId),
				]);

				setOrganization(orgResponse.organization);
				setRole(orgResponse.role);
				setUsers(usersResponse.users);
				setSubscription(orgResponse.subscription ?? null);
				setCreditWallet(orgResponse.creditWallet ?? null);
				if (orgResponse.subscription?.intervalType) {
					setIsYearly(orgResponse.subscription.intervalType === 'year');
				}
			} catch (error) {
				console.error('Failed to fetch organization data:', error);
				toast.error('Failed to load team');
				navigate(routes.profile());
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [organizationId, navigate]);

	const onChangeOrgName = useCallback(() => {
		if (!organizationId || !organization) return;

		displayPromptModal({
			title: 'Change Team Name',
			submitLabel: 'Save Changes',
			placeholder: 'Team name',
			initialValue: organization.name,
			validate: (value) => {
				const trimmed = value.trim();
				if (!trimmed) return 'Team name cannot be empty';
				if (trimmed.length > 100) return 'Team name must be 100 characters or less';
				return null;
			},
			onSubmit: async (newName) => {
				if (!newName?.trim()) return;
				try {
					await forge.updateOrganization(organizationId, {
						update: { id: organizationId, name: newName.trim() },
					});
					setOrganization((prev) => (prev ? { ...prev, name: newName.trim() } : prev));
					toast.success('Team name updated');
				} catch (error) {
					console.error('Failed to update organization:', error);
					toast.error('Failed to update team name');
				}
			},
		});
	}, [organizationId, organization]);

	const onAddMember = useCallback(async () => {
		if (!organizationId || !userEmail.trim()) return;

		setAdding(true);
		try {
			await forge.addOrganizationUser.withToast()(organizationId, userEmail.trim());
			const usersResponse = await forge.getOrganizationUsers(organizationId);
			setUsers(usersResponse.users);
			setMemberEmail('');
			setShowInviteRow(false);
		} catch (error) {
			console.error('Failed to add user:', error);
		} finally {
			setAdding(false);
		}
	}, [organizationId, userEmail]);

	const onRemoveMember = useCallback(
		async (userId: string) => {
			if (!organizationId) return;

			try {
				await forge.removeOrganizationUser.withToast()(organizationId, userId);
				setUsers((prev) => prev.filter((m) => m.userId !== userId));
				toast.success('Member removed');
			} catch (error) {
				console.error('Failed to remove user:', error);
			}
		},
		[organizationId]
	);

	const currentTier = subscription?.tier ?? Tier.FREE;
	const hasActiveSubscription = currentTier !== Tier.FREE;

	const renewalDate = subscription?.periodStartDate ? formatDate(subscription.periodStartDate) : '';

	const planBadgeLabel =
		currentTier === Tier.FREE ? 'Free Plan' : currentTier === Tier.TIER_1 ? 'Pro Plan' : 'Max Plan';

	const renderTierCards = () => {
		switch (currentTier) {
			case Tier.FREE:
				return (
					<>
						<TierCard
							variant="upgrade"
							tier={Tier.TIER_1}
							isYearly={isYearly}
							organizationId={organizationId}
						/>
						<TierCard
							variant="upgrade"
							tier={Tier.TIER_2}
							isYearly={isYearly}
							organizationId={organizationId}
						/>
					</>
				);
			case Tier.TIER_1:
				return (
					<>
						<TierCard
							tier={Tier.TIER_1}
							variant="current"
							renewalDate={renewalDate}
							organizationId={organizationId}
						/>
						<TierCard
							tier={Tier.TIER_2}
							variant="upgradeToMax"
							isYearly={isYearly}
							organizationId={organizationId}
						/>
					</>
				);
			case Tier.TIER_2:
				return (
					<>
						<TierCard
							tier={Tier.TIER_2}
							variant="current"
							renewalDate={renewalDate}
							organizationId={organizationId}
						/>
					</>
				);
			default:
				return null;
		}
	};

	if (loading) {
		return (
			<Base.Base>
				<Base.Container>
					<S.LoadingState>
						<Loader width={32} height={32} />
					</S.LoadingState>
				</Base.Container>
			</Base.Base>
		);
	}

	return (
		<Base.Base>
			<Base.Container>
				{isMobile && (
					<Base.SidebarToggle>
						<SidebarToggle />
					</Base.SidebarToggle>
				)}
				<Base.Content>
					<S.BackLink onClick={() => navigate(routes.profile())}>
						<Icon type={IconType.LINE_ARROW_LEFT} width={16} height={16} color={theme.gray[600]} />
						Back to profile
					</S.BackLink>

					<Header
						title={organization?.name ?? ''}
						email={role === 'owner' ? 'Owner' : 'Member'}
						planBadgeColor="blue"
						planBadgeLabel={planBadgeLabel}
						onEdit={isOwner ? onChangeOrgName : undefined}
						showSignOut={false}
					/>

					{creditWallet && (
						<Base.Section>
							<Base.SectionHeader>
								<Base.SectionTitle>Team Credits</Base.SectionTitle>
							</Base.SectionHeader>
							<Base.CreditDashboard>
								{isOwner ? (
									<>
										<CreditTile
											icon={IconType.REFRESH}
											iconColor={theme.blue[400]}
											label="Credits"
											amount={formatCredits(creditWallet.credits)}
											subtext={renewalDate ? `Renews on ${renewalDate}` : 'Part of subscription'}
										/>
										<CreditTile
											icon={IconType.GIFT}
											iconColor={theme.purple[500]}
											label="Promotional Credits"
											amount={formatCredits(creditWallet.promotionalCredits)}
											subtext="Awarded by admin"
										/>
										<CreditTile
											icon={IconType.CREDIT_CARD}
											iconColor={theme.green[400]}
											label="Top-up Credits"
											amount={formatCredits(creditWallet.purchaseCredits)}
											subtext="One-time purchases"
											button={{
												label: 'Buy More Credits',
												onClick: () =>
													window.open(
														`${VITE_FORGE_URL}/stripe/org-credit-purchase?organizationId=${organizationId}`
													),
												icon: IconType.ADD,
											}}
										/>
									</>
								) : (
									<CreditTile
										icon={IconType.REFRESH}
										iconColor={theme.blue[400]}
										label="Total Credits"
										amount={formatCredits(
											creditWallet.credits +
												creditWallet.promotionalCredits +
												creditWallet.purchaseCredits
										)}
										subtext="Team balance"
									/>
								)}
							</Base.CreditDashboard>
						</Base.Section>
					)}

					<Base.Section>
						<Base.SectionHeader>
							<Base.SectionTitle>Team Members</Base.SectionTitle>
							{isOwner && (
								<ColorButton
									label="Add Member"
									icon={IconType.ADD}
									color="blue"
									size="small"
									onClick={() => setShowInviteRow((v) => !v)}
								/>
							)}
						</Base.SectionHeader>
						{isOwner && showInviteRow && (
							<S.InviteRow>
								<S.InviteInput
									type="email"
									value={userEmail}
									onChange={(e) => setMemberEmail(e.target.value)}
									placeholder="Enter email address to add user"
									onKeyDown={(e) => {
										if (e.key === 'Enter' && userEmail.trim()) onAddMember();
									}}
								/>
								<ColorButton
									label={adding ? 'Adding...' : 'Add'}
									color="blue"
									size="small"
									onClick={onAddMember}
									disabled={!userEmail.trim() || adding}
								/>
							</S.InviteRow>
						)}
						<S.TeamCard>
							{users.length === 0 ? (
								<S.EmptyState>No users yet</S.EmptyState>
							) : (
								<S.MemberTable>
									<S.MemberTableHead>
										<tr>
											<S.MemberTableHeader>Member</S.MemberTableHeader>
											<S.MemberTableHeader>Role</S.MemberTableHeader>
											<S.MemberTableHeader>Actions</S.MemberTableHeader>
										</tr>
									</S.MemberTableHead>
									<S.MemberTableBody>
										{users.map((user) => (
											<S.MemberTableRow key={user.userId}>
												<S.MemberTableCell>
													<S.MemberInfo>
														<S.MemberAvatar
															style={{
																background: getAvatarGradient(user.displayName || 'Unknown', theme),
															}}
														>
															{getInitials(user.displayName || 'Unknown')}
														</S.MemberAvatar>
														<S.MemberDetails>
															<S.MemberName>{user.displayName || 'Unknown'}</S.MemberName>
															{user.email && <S.MemberEmail>{user.email}</S.MemberEmail>}
														</S.MemberDetails>
													</S.MemberInfo>
												</S.MemberTableCell>
												<S.MemberTableCell>
													<Chip
														label={user.role === 'owner' ? 'Owner' : 'Member'}
														color={user.role === 'owner' ? 'purple' : 'blue'}
													/>
												</S.MemberTableCell>
												<S.MemberTableCell>
													{isOwner && user.userId !== currentUser?.id && user.role !== 'owner' && (
														<S.RemoveButton onClick={() => onRemoveMember(user.userId)}>
															remove
														</S.RemoveButton>
													)}
												</S.MemberTableCell>
											</S.MemberTableRow>
										))}
									</S.MemberTableBody>
								</S.MemberTable>
							)}
						</S.TeamCard>
					</Base.Section>

					{isOwner && (
						<Base.SubscriptionSection>
							{hasActiveSubscription && <Base.TierCards>{renderTierCards()}</Base.TierCards>}
						</Base.SubscriptionSection>
					)}

					<S.SupportNote>
						Need help? Contact <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
					</S.SupportNote>
				</Base.Content>
			</Base.Container>
		</Base.Base>
	);
};

export default Organization;
